import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router, Redirect, Route, Switch
} from 'react-router-dom';

import Protected from 'components/Protected';
import UserScreenActivityTracker from 'libs/idle-user-detection';
import AppSettingsProvider from 'contexts/AppContext';
import ModalProvider from 'contexts/ModalContext';
import AuthLayout from './components/argonTemplate/layouts/Auth';
import AdminLayout from './components/argonTemplate/layouts/Admin';

import firebase from './libs/firebase';
import { asyncComponent } from './libs/helpers/index';

import User, { IUserType, UserRoles } from './types/profile.types';
import { ACTIVITY_TIMEOUT_DURATION } from './constants/index';
import storage from './services/storage.service';

import './App.scss';
import 'styles/style.css';
import 'styles/orderConfirmation.scss';
import { getUserRole } from './libs/utils/users';
import { getUserAuthData } from './services/brands.service';

const LoginPage = asyncComponent(() => import('routes/Login'));
const ForgotPasswordPage = asyncComponent(() => import('routes/ForgotPassword'));
const AuthActionsPage = asyncComponent(() => import('routes/AuthActions'));

const DashboardPage = asyncComponent(() => import('routes/Dashboard'));

const OCFormPage = asyncComponent(() => import('routes/OrderConfirmationForm'));
const BlankStockOrderPage = asyncComponent(
  () => import('routes/OrderConfirmationForm/BlankStockOrderForm')
);
const OCDraftPage = asyncComponent(() => import('routes/OrderConfirmationForm/draft'));
const OCCompletePage = asyncComponent(
  () => import('routes/OrderConfirmationForm/complete')
);

const LiveAssignedPage = asyncComponent(
  () => import('routes/StockManagement/LiveAssigned')
);
const InventoryUnassignedPage = asyncComponent(
  () => import('routes/StockManagement/InventoryUnassigned')
);
const StockPage = asyncComponent(() => import('routes/StockManagement/Stock'));
const CabageClearancePage = asyncComponent(() => import('routes/StockManagement/Cabage'));

const CriticalPathPage = asyncComponent(() => import('routes/CriticalPath'));

const DispatchedStockPage = asyncComponent(() => import('routes/DispatchedStock'));

const GroundedPage = asyncComponent(() => import('routes/ManagementDatabase/Grounded'));

const UsersListPage = asyncComponent(
  () => import('routes/ApplicationManagement/Users/Table')
);
const UserFormPage = asyncComponent(
  () => import('routes/ApplicationManagement/Users/Form')
);
const BrandsTablePage = asyncComponent(
  () => import('routes/ApplicationManagement/Brands/Table')
);
const BrandsFormPage = asyncComponent(
  () => import('routes/ApplicationManagement/Brands/Form')
);
const FactoryTablePage = asyncComponent(
  () => import('routes/ApplicationManagement/Factories/Table')
);
const FactoryFormPage = asyncComponent(
  () => import('routes/ApplicationManagement/Factories/Form')
);
const GroupTablePage = asyncComponent(
  () => import('routes/ApplicationManagement/Groups/Table')
);
const GroupFormPage = asyncComponent(
  () => import('routes/ApplicationManagement/Groups/Form')
);
const BlankStockProfileTablePage = asyncComponent(
  () => import('routes/ApplicationManagement/BlankStockProfiles/Table')
);
const BlankStockProfileFormPage = asyncComponent(
  () => import('routes/ApplicationManagement/BlankStockProfiles/Form')
);

const App = () => {
  const [currentAppUser, setCurrentAppUser] = useState<IUserType | null>(null);
  const [refreshToken, setRefreshToken] = useState(false);
  const [isUnauthenticated, setIsUnauthenticated] = useState(!storage.getToken());
  const authRoutePaths = ['/login', '/forgot-password', '/auth/actions'];

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (user: User | null) => {
      if (user !== null) {
        const userToken = await user.getIdTokenResult(true);

        storage.setToken(userToken.token);
        storage.setRefToken(user.refreshToken);

        // the fallback is to cater for existing users without firebase roles
        const role = getUserRole(userToken?.claims?.role) || UserRoles.GENERAL_USER;

        const userAuthData = await getUserAuthData({
          userFirebaseId: userToken?.claims?.sub,
        });
        setCurrentAppUser({
          ...user,
          role,
          id: userAuthData?.id,
          brandIds: userAuthData?.brandIds,
          brandUserIds: userAuthData?.brandUserIds,
        });
      } else {
        storage.clearToken();
        setIsUnauthenticated(true);
      }
    });

    const checkTokenExpiry = async () => {
      const { currentUser } = firebase.auth();

      if (currentUser) {
        console.log('Checking token expiry');

        const tokenResult = await currentUser.getIdTokenResult(true);
        storage.setToken(tokenResult.token);

        const nowTime = new Date().getTime();
        const issueTime = new Date(tokenResult.issuedAtTime).getTime();
        const expireTime = new Date(tokenResult.expirationTime).getTime();

        const tokenHasExpired = nowTime - issueTime + expireTime > expireTime;

        if (tokenHasExpired) {
          setRefreshToken(!refreshToken);
        }
      }
    };

    const tracker = new UserScreenActivityTracker({
      awayTimeout: ACTIVITY_TIMEOUT_DURATION,
      onAway: () => checkTokenExpiry(),
      onAwayBack: () => checkTokenExpiry(),
      onVisible: () => {
        if (tracker.isUserAway) {
          checkTokenExpiry();
        }
      },
      onHidden: () => {
        if (!tracker.isUserAway) {
          checkTokenExpiry();
        }
      },
    });

    // start the tracker
    // tracker.start();

    return () => {
      // tracker.stop();
      unsubscribe();
    };
  }, [refreshToken, setIsUnauthenticated]);

  return (
    <Router>
      <Switch>
        <ModalProvider>
          {!!currentAppUser && !authRoutePaths.includes(window.location.pathname) ? (
            <AdminLayout role={currentAppUser.role}>
              <AppSettingsProvider>
                {/* Dashboard view */}
                <Route
                  exact
                  path="/"
                  render={(routeProps) => (
                    <DashboardPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                <Route
                  exact
                  path="/dashboard"
                  render={(routeProps) => (
                    <DashboardPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Order Confirmation new form */}
                <Route
                  exact
                  path="/orderconfirmation/new"
                  render={(routeProps) => (
                    <OCFormPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Order Confirmation edit form */}
                <Route
                  exact
                  path="/orderconfirmation/edit/:id"
                  render={(routeProps) => (
                    <OCFormPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Draft Order Confirmation table */}
                <Route
                  exact
                  path="/orderconfirmation/view/draft"
                  render={(routeProps) => (
                    <OCDraftPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Amend / Repeat / Complete Order Confirmations table */}
                <Route
                  exact
                  path="/orderconfirmation/view"
                  render={(routeProps) => (
                    <OCCompletePage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Blank Stock Order Form */}
                <Route
                  component={BlankStockOrderPage}
                  exact
                  path="/orderconfirmation/new/quantity/:ocdrop"
                />

                {/* Blank Stock Order Form - Edit */}
                <Route
                  component={BlankStockOrderPage}
                  exact
                  path="/orderconfirmation/edit/:ocId/quantity/:ocdrop"
                />

                {/* Critical Path */}
                <Route
                  exact
                  path="/criticalpath/uk"
                  render={(routeProps) => (
                    <CriticalPathPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />
                <Route
                  exact
                  path="/criticalpath/turkey"
                  render={(routeProps) => (
                    <CriticalPathPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />
                {/* Dispatched Stock */}
                <Route
                  exact
                  path="/dispatchedstock"
                  render={(routeProps) => (
                    <DispatchedStockPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Grounded / GRND */}
                <Route
                  exact
                  path="/managementdatabase/grnd"
                  render={(routeProps) => (
                    <GroundedPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Inventory (Unassigned) */}
                <Route
                  exact
                  path="/stockmanagement/unassigned"
                  render={(routeProps) => <InventoryUnassignedPage {...routeProps} />}
                />

                {/* Create Stock - New */}
                <Route
                  exact
                  path="/stockmanagement/stock/new"
                  render={(routeProps) => <StockPage {...routeProps} />}
                />

                {/* Edit Stock */}
                <Route
                  exact
                  path="/stockmanagement/stock/edit/:bsodId"
                  render={(routeProps) => <StockPage {...routeProps} />}
                />

                {/* Cabage (Clearance) */}
                <Route
                  exact
                  path="/stockmanagement/cabage"
                  render={(routeProps) => <CabageClearancePage {...routeProps} />}
                />

                {/* Stock management - Live */}
                <Route
                  exact
                  path="/stockmanagement/live"
                  render={(routeProps) => (
                    <LiveAssignedPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Application Management - Users */}
                <Route
                  exact
                  path="/applicationmanagement/users/list"
                  render={(routeProps) => (
                    <Protected
                      action="applicationmanagement:visit"
                      no={() => <Redirect to="/dashboard" />}
                      role={currentAppUser.role || ''}
                      yes={() => (
                        <UsersListPage
                          {...routeProps}
                          user={currentAppUser}
                        />
                      )}
                    />
                  )}
                />
                <Route
                  component={UserFormPage}
                  exact
                  path="/applicationmanagement/users/new"
                />
                <Route
                  component={UserFormPage}
                  exact
                  path="/applicationmanagement/users/edit/:userId"
                />

                {/* Application Management - Brands */}
                <Route
                  exact
                  path="/applicationmanagement/brands/list"
                  render={(routeProps) => (
                    <Protected
                      action="applicationmanagement:visit"
                      no={() => <Redirect to="/dashboard" />}
                      role={currentAppUser.role || ''}
                      yes={() => (
                        <BrandsTablePage
                          {...routeProps}
                          user={currentAppUser}
                        />
                      )}
                    />
                  )}
                />

                <Route
                  component={BrandsFormPage}
                  exact
                  path="/applicationmanagement/brands/new"
                />

                <Route
                  exact
                  path="/applicationmanagement/brands/edit/:brandId"
                  render={(routeProps) => (
                    <BrandsFormPage
                      {...routeProps}
                      user={currentAppUser}
                    />
                  )}
                />

                {/* Application Management - Factories */}
                <Route
                  exact
                  path="/applicationmanagement/factories/list"
                  render={(routeProps) => (
                    <Protected
                      action="applicationmanagement:visit"
                      no={() => <Redirect to="/dashboard" />}
                      role={currentAppUser.role || ''}
                      yes={() => (
                        <FactoryTablePage
                          {...routeProps}
                          user={currentAppUser}
                        />
                      )}
                    />
                  )}
                />
                <Route
                  component={FactoryFormPage}
                  exact
                  path="/applicationmanagement/factories/new"
                />
                <Route
                  component={FactoryFormPage}
                  exact
                  path="/applicationmanagement/factories/edit/:factoryId"
                />

                {/* Application Management - Groups */}
                <Route
                  exact
                  path="/applicationmanagement/groups/list"
                  render={(routeProps) => (
                    <Protected
                      action="applicationmanagement:visit"
                      no={() => <Redirect to="/dashboard" />}
                      role={currentAppUser.role || ''}
                      yes={() => <GroupTablePage {...routeProps} />}
                    />
                  )}
                />
                <Route
                  component={GroupFormPage}
                  exact
                  path="/applicationmanagement/groups/new"
                />
                <Route
                  component={GroupFormPage}
                  exact
                  path="/applicationmanagement/groups/edit/:groupId"
                />

                {/* Application Management - Blank Stock Profiles */}
                <Route
                  exact
                  path="/applicationmanagement/blankstockprofiles/list"
                  render={(routeProps) => (
                    <Protected
                      action="applicationmanagement:visit"
                      no={() => <Redirect to="/dashboard" />}
                      role={currentAppUser.role || ''}
                      yes={() => <BlankStockProfileTablePage {...routeProps} />}
                    />
                  )}
                />
                <Route
                  component={BlankStockProfileFormPage}
                  exact
                  path="/applicationmanagement/blankstockprofiles/new"
                />
                <Route
                  component={BlankStockProfileFormPage}
                  exact
                  path="/applicationmanagement/blankstockprofiles/edit/:blankStockProfileId"
                />
              </AppSettingsProvider>
            </AdminLayout>
          ) : (
            <AuthLayout>
              <Route
                component={LoginPage}
                exact
                path="/login"
              />
              <Route
                component={ForgotPasswordPage}
                exact
                path="/forgot-password"
              />
              <Route
                exact
                path="/auth/actions"
                render={(routeProps) => <AuthActionsPage {...routeProps} />}
              />

              {/* Fallback route */}
              {isUnauthenticated
                && !currentAppUser
                && !authRoutePaths.includes(window.location.pathname) && (
                  <Redirect
                    to={{
                      pathname: '/login',
                      state: {
                        redirectUri: window.location.pathname,
                      },
                    }}
                  />
                )}
            </AuthLayout>
          )}
        </ModalProvider>
      </Switch>
    </Router>
  );
};

export default App;
